import map from 'lodash/map';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import {
  Badge,
  Card,
  CardBody,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Row,
  Table,
  UncontrolledDropdown,
} from 'reactstrap';

// Import Breadcrumb
import Breadcrumbs from 'components/Common/Breadcrumb';

// Import Image
import { Divider } from '@material-ui/core';
import Loader from 'components/Loader';
import TableError from 'components/TableError';
import { convertFromRaw, EditorState } from 'draft-js';
import useRequest from 'hooks/useRequest';
import { useRouter } from 'hooks/useRouter';
import { isEmpty } from 'lodash';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { createStructuredSelector } from 'reselect';
import Paginate from '../../../components/Paginate';
import {
  ACTIVE_AND_DELETED_ARRAY,
  LANGUAGE_ARRAY_WITH_STRING,
  PRINT_LANGUAGE,
  PRINT_LANGUAGE_COLOR,
  SORT_VALUES,
  TOPIC_STATUS_STING,
} from '../../../utils/constants';
import { buildURL, checkIfValueExist, printDateTimeTwoLines } from '../../../utils/helper';
import { SMKLinks } from '../../../utils/links';
import { getForumTopic } from '../actions';
import { topicListPaginationSelector, topicListSelector } from '../selectors';

const stateSelector = createStructuredSelector({
  topics: topicListSelector,
  topicsPagination: topicListPaginationSelector,
});

const TopicsList = (props) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const history = useHistory();

  const { topics, topicsPagination } = useSelector(stateSelector);
  toast.configure();

  const [markApproveAndUnpublishRequest, { loading: markApproveAndUnpublishLoading }] = useRequest({
    method: 'PUT',
    onSuccess: (data) => {
      handleChangePage(parseInt(router?.query?.page, 10) || 1);
      toast.success(`Success! Topic has been ${data?.status !== 0 ? 'UnPublished' : 'Approved'}.`);
    },
  });

  const [deleteUnDeleteRequest, { loading: deleteUnDeleteLoading }] = useRequest({
    method: 'PUT',
    onSuccess: (data) => {
      handleChangePage(parseInt(router?.query?.page, 10) || 1);
      toast.success(`Success!  Topic has been ${data?.deleted ? 'deleted' : 'Undeleted'}.`);
    },
  });

  const defaultFilters = {
    active: { value: null, label: 'All' },
    deleted: { value: null, label: 'All' },
    language: LANGUAGE_ARRAY_WITH_STRING[0]?.value,
    id: '',
    title: '',
  };

  const [filters, setFilters] = useState(defaultFilters);

  const setPageInUrl = (page) => {
    history.push({
      pathname: router.pathname,
      search: `?${new URLSearchParams({ page }).toString()}`,
    });
  };

  const handleChangePage = (page) => {
    setPageInUrl(page);
    dispatch(
      getForumTopic.trigger({
        page,
        size: 20,
        ...filters,
        ...SORT_VALUES,
        active: checkIfValueExist(filters?.active?.value),
        deleted: checkIfValueExist(filters?.deleted?.value),
      })
    );
  };

  const applyFilters = () => {
    handleChangePage(parseInt(router?.query?.page, 10) || 1);
  };

  const clearFilters = () => {
    setFilters({
      ...defaultFilters,
    });
    dispatch(
      getForumTopic.trigger({
        page: 1,
        size: 20,
        ...defaultFilters,
        ...SORT_VALUES,
        active: checkIfValueExist(defaultFilters?.active?.value),
        deleted: checkIfValueExist(defaultFilters?.deleted?.value),
      })
    );
  };

  useEffect(() => {
    handleChangePage(parseInt(router?.query?.page, 10) || 1);
  }, []);

  const topicStatusColors = {
    0: 'bg-warning',
    1: 'bg-success',
    2: 'bg-danger',
  };

  const handleApprove = (data) => {
    data?.status !== 0
      ? markApproveAndUnpublishRequest({
          url: `/forum/admin/topic/unpublish`,
          body: {
            id: data?.id,
          },
        })
      : markApproveAndUnpublishRequest({
          url: `/forum/admin/topic/approve`,
          body: {
            id: data?.id,
            status: 1,
            withReAssignment: true,
            comments: data?.comments,
          },
        });
  };
  const handleDelete = (data) => {
    deleteUnDeleteRequest({
      url: `/forum/admin/topic/delete`,
      body: {
        id: data?.id,
        deleted: !data?.deleted,
      },
    });
  };

  return (
    <>
      <div className="page-content">
        <Loader isActive={deleteUnDeleteLoading || markApproveAndUnpublishLoading} />
        <Container fluid>
          {/* Render Breadcrumbs */}
          <Breadcrumbs title="Topics" breadcrumbItem="Topics List" />

          <Card>
            <CardBody>
              <div className="mt-4">
                <div className="row">
                  <div className="col-lg-2 mb-4">
                    <label className="form-label">Active</label>
                    <Select
                      options={ACTIVE_AND_DELETED_ARRAY}
                      value={filters.active}
                      onChange={(value) => setFilters({ ...filters, active: value })}
                    />
                  </div>
                  <div className="col-lg-2 mb-4">
                    <label className="form-label">Deleted</label>
                    <Select
                      options={ACTIVE_AND_DELETED_ARRAY}
                      value={filters.deleted}
                      onChange={(value) => setFilters({ ...filters, deleted: value })}
                    />
                  </div>
                  <div className="col-lg-2 mb-4">
                    <label className="form-label">Language</label>
                    <Select
                      options={LANGUAGE_ARRAY_WITH_STRING}
                      value={LANGUAGE_ARRAY_WITH_STRING?.find((t) => t.value == filters?.language)}
                      onChange={(item) =>
                        setFilters({
                          ...filters,
                          language: item.value,
                        })
                      }
                    />
                  </div>
                  <div className="col-lg-3 mb-4">
                    <label className="form-label">Topic ID</label>
                    <Input
                      type="number"
                      placeholder="Topic ID..."
                      value={filters.id}
                      onChange={(e) => setFilters({ ...filters, id: e?.target?.value })}
                    />
                  </div>
                  <div className="col-lg-3 mb-4">
                    <label className="form-label">Title</label>
                    <Input
                      type="text"
                      placeholder="Title..."
                      value={filters.title}
                      onChange={(e) => setFilters({ ...filters, title: e?.target?.value })}
                    />
                  </div>
                </div>
              </div>

              <div className="mt-4">
                <a className="btn btn-primary me-4" onClick={applyFilters}>
                  Apply Filters
                </a>
                <a className="" onClick={clearFilters}>
                  Clear
                </a>
              </div>
            </CardBody>
          </Card>

          <Row className="mb-2">
            <Col sm="4">
              <div className="search-box me-2 mb-2 d-inline-block">
                <div className="position-relative font-bold">{topicsPagination?.totalCount} Topics Found</div>
              </div>
            </Col>
          </Row>

          <Row>
            <Col lg="12">
              <div className="">
                <div className="table-responsive" style={{ minHeight: 300 }}>
                  <Table className="project-list-table table-nowrap table-centered table-borderless">
                    <thead>
                      <tr className="table-warning">
                        <th scope="col">Topic Id</th>
                        <th scope="col">
                          Title
                          <br /> Slug
                        </th>
                        <th scope="col">Created By</th>
                        <th scope="col">Created On</th>
                        <th scope="col">
                          View <br /> Count
                        </th>
                        <th scope="col">
                          Follow <br /> Count
                        </th>
                        <th scope="col">Language</th>
                        <th scope="col">Status</th>
                        <th scope="col">Is Featured</th>
                        <th scope="col">Active</th>
                        <th scope="col">Deleted</th>
                        <th scope="col" />
                      </tr>
                    </thead>
                    <tbody>
                      {!isEmpty(topics) ? (
                        map(topics, (topic, index) => {
                          let removeEditor = false;
                          let parsedContent = { blocks: [], entityMap: {} };
                          try {
                            parsedContent = !isEmpty(JSON.parse(topic?.jsonContent))
                              ? JSON.parse(topic?.jsonContent?.replaceAll('\\', ''))
                              : { blocks: [], entityMap: {} };
                          } catch (err) {
                            removeEditor = false;
                          }

                          return (
                            <React.Fragment key={index}>
                              <tr>
                                <td>
                                  <Link
                                    to={
                                      buildURL(SMKLinks.TOPIC_DETAIL, {
                                        id: topic.id,
                                      }) + `?active=${topic?.active}`
                                    }>
                                    {topic.id}
                                  </Link>
                                </td>

                                <td style={{ maxWidth: 800, whiteSpace: 'pre-wrap' }}>
                                  <Link
                                    to={buildURL(SMKLinks.TOPIC_EDIT, {
                                      id: topic?.id,
                                    })}
                                    className="text-primary">
                                    {topic.title}
                                  </Link>
                                  <br />
                                  {topic.slug}
                                </td>
                                <td>
                                  <div className="text-truncate ">
                                    {topic.createdBy ? topic.createdBy.firstName : ''}
                                  </div>
                                </td>
                                <td>{printDateTimeTwoLines(topic.createdDate)}</td>
                                <td>{topic?.viewCount}</td>
                                <td>{topic?.followCount}</td>
                                <td>
                                  <Badge className={PRINT_LANGUAGE_COLOR[topic?.language]}>
                                    {PRINT_LANGUAGE[topic?.language]}
                                  </Badge>
                                </td>
                                <td>
                                  <Badge className={` ${topicStatusColors[topic?.status]}`}>
                                    {TOPIC_STATUS_STING[topic.status]}
                                  </Badge>
                                </td>
                                <td align="center">
                                  <Badge className={topic?.isFeatured ? 'bg-success' : 'bg-danger'}>
                                    {topic?.isFeatured ? 'Yes' : 'No'}
                                  </Badge>
                                </td>
                                <td>
                                  <Badge className={topic?.active ? 'bg-success' : 'bg-danger'}>
                                    {topic?.active ? 'Yes' : 'No'}
                                  </Badge>
                                </td>
                                <td>
                                  <Badge className={topic?.deleted ? 'bg-success' : 'bg-danger'}>
                                    {topic?.deleted ? 'Yes' : 'No'}
                                  </Badge>
                                </td>
                                <td>
                                  <UncontrolledDropdown>
                                    <DropdownToggle href="#" className="card-drop cursor-pointer" tag="i">
                                      <i role="button" className="mdi mdi-dots-vertical font-size-18 " />
                                    </DropdownToggle>
                                    <DropdownMenu className="dropdown-menu-end">
                                      <DropdownItem onClick={() => handleApprove(topic)}>
                                        <i
                                          className={`fas ${
                                            topic?.status !== 0
                                              ? 'fa-trash-alt text-danger'
                                              : 'fa-power-off text-success'
                                          }     m-1 pe-3`}
                                        />
                                        {topic?.status !== 0 ? 'Unpublish' : 'Approve'}
                                      </DropdownItem>

                                      {!topic?.deleted && (
                                        <DropdownItem onClick={() => handleDelete(topic)}>
                                          <i
                                            className={`fas fa-trash-alt ${
                                              topic?.deleted ? 'text-success' : 'text-danger'
                                            } m-1 pe-3`}
                                          />
                                          {topic?.deleted ? 'Undelete' : 'Delete'}
                                        </DropdownItem>
                                      )}

                                      <Divider className="my-2" />

                                      <DropdownItem
                                        href={
                                          buildURL(SMKLinks.TOPIC_DETAIL, {
                                            id: topic.id,
                                          }) + `?active=${topic?.active}`
                                        }>
                                        <i className="fas fa-eye text-primary m-1 pe-3 " />
                                        Details
                                      </DropdownItem>
                                      <DropdownItem
                                        href={buildURL(SMKLinks.TOPIC_EDIT, {
                                          id: topic.id,
                                        })}>
                                        <i className="fas fa-pencil-alt text-success m-1 pe-3 " />
                                        Edit
                                      </DropdownItem>
                                    </DropdownMenu>
                                  </UncontrolledDropdown>
                                </td>
                              </tr>
                              {!removeEditor && (
                                <tr>
                                  <td></td>
                                  <td colSpan={9} style={{ maxWidth: 1000, whiteSpace: 'pre-wrap' }}>
                                    <Editor
                                      editorState={EditorState.createWithContent(convertFromRaw(parsedContent))}
                                      toolbarClassName="toolbarClassName"
                                      wrapperClassName="wrapperClassName"
                                      editorClassName="editorClassName"
                                      readOnly={true}
                                      editorStyle={{
                                        padding: '20px',
                                      }}
                                    />
                                  </td>
                                  <td colSpan={20}></td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })
                      ) : (
                        <TableError />
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>
            </Col>
          </Row>
          <Paginate pageInfo={topicsPagination} onChangePage={handleChangePage} />
        </Container>
      </div>
    </>
  );
};

export default TopicsList;
